/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {useMutation} from 'react-query';
import {createGroup} from '@ion/core/src/services/Connect';
import {CTCEntity, Link} from '@ion/core/src/types/entities';
import {createMutationResult} from '../utils/helpers';

export interface UseNewGroupParams {
  account: Link | CTCEntity;
  name: string;
  notes: string;
};

export type UseNewGroupResult = {
  newGroupId: number;
  createGroup: (params: UseNewGroupParams, callbacks) => void;
}

/**
 * The UseNewGroup hook is used to retrieve new group id.
 *
 * ```typescript
 *
 *  import {UseNewGroup} from '@ion/packages/query/src/hooks';
 *
 *  const {newGroupId} = UseNewGroup({
 *    account,
 *    name,
 *    notes
 *  });
 *
 * ```
 */
export const useNewGroup = (): UseNewGroupResult => {
  const mutation = useMutation(async(params: UseNewGroupParams) => {
    const {
      account,
      name,
      notes
    } = params;
    const payload = {
      account,
      name,
      notes
    };
    const result = await createGroup(payload);
    if (result) {
      return result?.id;
    }
    return '';
  });

  return createMutationResult(
    mutation, {
      createGroup : mutation.mutate
    }
  ) as UseNewGroupResult;
};

export default useNewGroup;
